import React, { useCallback, useEffect, useState } from "react";
import { UserCheck, RefreshCw, Clock, Check, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly, formatDateTime } from "@/lib/date";

const STATUS_META = {
  present: { label: "Present", cls: "bg-emerald-100 text-emerald-700" },
  absent: { label: "Absent", cls: "bg-red-100 text-red-700" },
  tardy: { label: "Tardy", cls: "bg-amber-100 text-amber-700" },
  excused: { label: "Excused", cls: "bg-sky-100 text-sky-700" },
};

export const STATUS_OPTIONS = [
  { key: "present", label: "Present", Icon: Check, on: "bg-emerald-600 text-white" },
  { key: "tardy", label: "Tardy", Icon: Clock, on: "bg-amber-500 text-white" },
  { key: "absent", label: "Absent", Icon: X, on: "bg-red-600 text-white" },
  { key: "excused", label: "Excused", Icon: ShieldCheck, on: "bg-sky-600 text-white" },
];

// Attendance sheet for a single schedule event. Everyone defaults to Present; the
// taker marks exceptions with the Present/Tardy/Absent control. Submitting stamps the time.
export default function AttendanceSheetDialog({ event, open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const eventId = event?.id;

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/event/${eventId}`);
      setMeta(data);
      setRows(data.rows || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); onClose?.(); }
    finally { setLoading(false); }
  }, [eventId, onClose]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const setStatus = (id, status) => setRows((rs) => rs.map((r) => (r.member_id === id ? { ...r, status } : r)));
  const setNote = (id, note) => setRows((rs) => rs.map((r) => (r.member_id === id ? { ...r, note } : r)));

  const presentCount = rows.filter((r) => r.status === "present").length;
  const exceptions = rows.length - presentCount;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/attendance/event/${eventId}`, {
        records: rows.map((r) => ({ member_id: r.member_id, status: r.status, note: r.note || "" })),
      });
      toast.success(`Attendance saved — ${data.present} present, ${data.tardy} tardy, ${data.absent} absent${data.excused ? `, ${data.excused} excused` : ""}.`);
      onSaved?.();
      onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="attendance-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><UserCheck className="h-5 w-5 text-orange-500" /> Attendance</DialogTitle>
          <DialogDescription>{event ? `${event.title} · ${formatDateOnly(event.event_date)}` : ""}</DialogDescription>
        </DialogHeader>

        {meta?.taken && meta?.submitted_at && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="attendance-last-taken">
            Last taken {formatDateTime(meta.submitted_at, { dateStyle: "medium", timeStyle: "short" })}
            {meta.taken_by_name ? ` by ${meta.taken_by_name}` : ""}. Submitting again overwrites it.
          </p>
        )}
        <p className="text-xs text-slate-500">Everyone is <span className="font-semibold text-emerald-700">Present</span> by default — just mark anyone who's tardy or absent.</p>

        {loading ? (
          <div className="flex justify-center py-10 text-slate-400"><RefreshCw className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="attendance-empty">No team members are assigned to this event.</p>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1" data-testid="attendance-rows">
            {rows.map((r) => {
              const sm = STATUS_META[r.status] || STATUS_META.present;
              const isPresent = r.status === "present";
              return (
                <div key={r.member_id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`attendance-row-${r.member_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2" data-testid={`attendance-name-${r.member_id}`}>
                      <span className="truncate font-medium text-slate-800">{r.member_name}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${sm.cls}`} data-testid={`attendance-status-${r.member_id}`}>{sm.label}</span>
                    </div>
                    <div className="flex shrink-0 flex-wrap rounded-full border border-slate-200 p-0.5" role="radiogroup">
                      {STATUS_OPTIONS.map(({ key, label, Icon, on }) => (
                        <button key={key} type="button" role="radio" aria-checked={r.status === key}
                          onClick={() => { setStatus(r.member_id, key); if (key === "present") setNote(r.member_id, ""); }}
                          className={`flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-semibold transition-colors ${r.status === key ? on : "text-slate-500 hover:bg-slate-100"}`}
                          data-testid={`attendance-${key}-${r.member_id}`}>
                          <Icon className="h-3.5 w-3.5" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {r.conflict != null && (
                    <p className="mt-2 rounded-md bg-sky-50 px-2 py-1 text-xs text-sky-700" data-testid={`attendance-conflict-${r.member_id}`}>
                      <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />Approved conflict: {r.conflict || "—"}
                    </p>
                  )}
                  {!isPresent && (
                    <Input value={r.note || ""} onChange={(e) => setNote(r.member_id, e.target.value)} placeholder="Optional note (e.g. excused absence)" className="mt-2 h-8 text-sm" data-testid={`attendance-note-${r.member_id}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-slate-500" data-testid="attendance-summary">{presentCount} present · {exceptions} exception{exceptions === 1 ? "" : "s"}</p>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={() => onClose?.()} data-testid="attendance-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || loading || rows.length === 0} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="attendance-submit-btn">
            {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Submit attendance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
